import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import getPath from 'lodash/get';
import styled from 'styled-components';
import InfiniteScroll from 'react-infinite-scroller';
import RatingStar from 'react-rating';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchManagedSoundcastRatingsListCreator,
  deleteManagedSoundcastRatingsCreator,
} from '../../../../routines';
import { FlexDiv, FlexiRow } from '../../../../styles/styled';
import { ratingListSelector } from '../../../../selectors/ratings';
import { ScrollableContent } from '../managed/styled';
import Colors from '../../../../styles/colors';
import CustomDialog from '../custom_dialog';
import { ActionButton } from '../../../../components/buttons/buttons';
import Loading from '../../../../components/Loading';
import { PAGINATED_RATINGS_LIMIT } from '../../../../helpers/episodes';
import Mailto from '../../../../components/MailTo';
const { cloudfrontToS3Url } = require('../../../../../server/scripts/utils')();

const Reviews = props => {
  const { soundcast } = props;
  if (isEmpty(soundcast)) {
    return null;
  }

  const alertDialog = useRef(null);
  const scroller = useRef(null);
  const { soundcastId } = soundcast;
  const dispatch = useDispatch();
  const ratings = useSelector(state => ratingListSelector(state, soundcastId));
  const total = useSelector(state => getPath(state, `ratings.byTotal.${soundcastId}`, 0));
  const sbusy = useSelector(state => getPath(state, `ratings.busy.${soundcastId}`, false));
  const [page, setPage] = useState(1);
  const [oldSoundcastId, setOldSoundcastId] = useState('');

  if (!isEqual(oldSoundcastId, soundcastId)) {
    console.log('SOUNDCAST ID NOT EQUAL', oldSoundcastId, soundcastId);
    setOldSoundcastId(soundcastId);
    setPage(1);
  }

  useEffect(() => {
    console.log('THE NEXT PAGE IS', page);
  }, [page]);

  useEffect(() => {
    // calculate the page start based on ratings result
    if (!isEmpty(ratings)) {
      const page = Math.ceil(ratings.length / PAGINATED_RATINGS_LIMIT) + 1;
      setPage(page);
    }
  }, [soundcastId, ratings]);

  useEffect(() => {
    // fetch ratings when its empty
    if (isEmpty(ratings)) {
      fetchRatings();
    }

    // reset list scroller on every change of soundcast
    if (scroller.current) {
      scroller.current.scrollTop = 0;
    }
  }, [soundcastId]);

  const fetchRatings = useCallback(async () => {
    fetchManagedSoundcastRatingsListCreator({ soundcastId, page }, dispatch);
    // setPage(page => page + 1);
  }, [soundcastId, page]);

  const onCloseModal = () => alertDialog.current.closeModal();

  const deleteRating = ratingId => () => {
    onCloseModal();
    deleteManagedSoundcastRatingsCreator({ soundcastId, ratings: [ratingId] }, dispatch);
  };

  const onRatingDelete = useCallback(
    ratingId => () => {
      alertDialog.current.showModal({
        content: 'Are you sure you want to delete this rating? This action cannot be undone.',
        actions: [
          <div className="padding-20px text-center">
            <ActionButton onClick={deleteRating(ratingId)}>Confirm</ActionButton>
            <ActionButton onClick={onCloseModal} textColor="#f76b1c" backgroundColor="#fff">
              Cancel
            </ActionButton>
          </div>,
        ],
      });
    },
    [soundcastId]
  );

  const hasMorePaginatedRatings = () => {
    // this will avoid the next call to `loadMoreRatings` function
    // when the component updates loading state changes
    // failed to do so will keep the infinite scroll component
    // to call the said function and increase the page count more often
    if (sbusy) return false;
    if (isEmpty(ratings)) return true;
    return total > ratings.length;
  };

  if (isEmpty(ratings)) {
    return <span className="text-medium">No reviews yet.</span>;
  }

  return (
    <>
      <RatingsScrollable ref={scroller}>
        <InfiniteScroll
          initialLoad={false}
          loadMore={fetchRatings}
          hasMore={hasMorePaginatedRatings()}
          useWindow={false}>
          {ratings.map((rating, index) => {
            const {
              ratingId,
              avatar = '',
              author = '',
              email = '',
              stars = 0,
              review = '',
              createdAt = '',
            } = rating;
            const created = moment(createdAt).format('MMM DD, YYYY, hh:mm');
            const createdfromNow = moment(createdAt).fromNow();
            const contentClassName = classNames({ 'margin-bottom-5px': !isEmpty(review) });
            return (
              <FlexiRow key={`${soundcastId}-rating-${index}`}>
                <RatingWrapper className="padding-20px img-rounded">
                  <FlexDiv alignItems="center" justifyContent="space-between">
                    <RatingAuthor alignItems="center" className="margin-bottom-5px">
                      <Avatar className="avatar img-circle border-1-light-gray" image={avatar} />
                      <span className="name text-large font-weight-600 margin-one-left">
                        {author}
                      </span>
                    </RatingAuthor>
                    {!isEmpty(email) && <Mailto email={email}>Contact</Mailto>}
                  </FlexDiv>
                  <div className={contentClassName}>
                    <RatingStar
                      initialRating={Number(stars)}
                      emptySymbol="far fa-star title-small md-title-small sm-title-small xs-title-small"
                      fullSymbol="fas fa-star title-small md-title-small sm-title-small xs-title-small"
                      readonly
                    />
                    <span className="font-weight-600 padding-left-10px text-large">
                      {Number(stars).toFixed(1)}
                    </span>
                  </div>
                  {!isEmpty(review) && <div className="review text-large">{review}</div>}
                  <div className="name text-small font-weight-400" title={created}>
                    {createdfromNow}
                  </div>
                </RatingWrapper>
              </FlexiRow>
            );
          })}
        </InfiniteScroll>
      </RatingsScrollable>
      <CustomDialog ref={alertDialog} />
    </>
  );
};

const RatingWrapper = styled.div`
  width: 100%;
  background-color: #f5f5f5;
  margin-bottom: 10px;

  .far,
  .fas {
    color: ${Colors.mainOrange};
  }

  a {
    color: #00bcd4;
  }
`;

const Avatar = styled.div`
  background-image: url(${props =>
    props.image
      ? cloudfrontToS3Url(props.image)
      : 'https://soundwiseinc.s3.amazonaws.com/user_profile_pic_placeholder.png'});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
`;

const RatingAuthor = styled(FlexDiv)`
  flex: 1 1 0%;
  ${Avatar} {
    width: 30px;
    height: 30px;
  }
`;

const RatingsScrollable = styled(ScrollableContent)`
  margin: 0;
  padding-right: 10px;
`;

Reviews.propTypes = {
  soundcast: PropTypes.object,
};

Reviews.defaultProps = {
  soundcast: {},
};

export default Reviews;
